import {useState, useEffect, useRef} from 'react';
import {useParams, useNavigate} from 'react-router';
import TimezoneSelect from 'react-timezone-select';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with timezone support so we can convert the user-selected
// timezone (from the form picker) to a correct UTC ISO timestamp.
dayjs.extend(utc);
dayjs.extend(timezone);

import ConfirmModal from '../components/ConfirmModal';
import {useAuth} from '../hooks/AuthContext';
import * as AppointmentApi from '../api/AppointmentApi';
import PageShell from '../components/PageShell';
import './AppointmentAdd.css'; // using the same styling as Add
import {problemMessage} from '../api/client';
import ChoiceRow from '../components/ChoiceRow';
import {dayRange, localDate, localTime, rescheduleOptions, toInstant} from '../components/rescheduleSlots';

const CONFLICT_MESSAGE = 'That time slot was just booked. Please choose another time.';

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const errorStyle = {color: '#B91C1C', fontSize: '12px'};
const alertBoxStyle = {
  color: '#991B1B',
  background: '#FEF2F2',
  border: '1px solid #FCA5A5',
  borderRadius: '12px',
  padding: '12px 14px',
  fontSize: '14px',
  whiteSpace: 'pre-line',
};

export default function AppointmentEditor() {
  const auth = useAuth();
  const {appointmentId} = useParams();
  const navigate = useNavigate();
  const [accountData] = useState(auth.user);
  const hasPhone = Boolean(
    accountData &&
    accountData.phone &&
    accountData.phone !== 'null' &&
    String(accountData.phone).trim() !== '',
  );
  const [confirmModalShow, setConfirmModalShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // The appointment as loaded (new-style shape), used to send only changed fields.
  const [original, setOriginal] = useState(null);

  const [appointment, setAppointment] = useState({
    date: '',
    time: '',
    timezone: browserTimezone(),
    note: '',
    email_alert: false,
    sms_alert: false,
    alert_before: [],
  });

  const [errors, setErrors] = useState({});
  const [conflictError, setConflictError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Availability for the chosen day.
  const [doctors, setDoctors] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [slotsError, setSlotsError] = useState(null);
  const [availabilityVersion, setAvailabilityVersion] = useState(0);
  const timeSelectRef = useRef(null);

  useEffect(() => {
    AppointmentApi.getAppointmentById(appointmentId)
      .then((data) => {
        const tz = data.patient_timezone || data.timezone || browserTimezone();
        const start = data.start_time || data.appointment_date;
        const alertBefore = AppointmentApi.alertBeforeLabels(start, data.reminder_alerts || []);
        setOriginal({
          ...data,
          patient_timezone: tz,
          notes: data.notes ?? data.note ?? '',
          alert_before: alertBefore,
        });
        setAppointment({
          date: localDate(start, tz),
          time: localTime(start, tz),
          timezone: tz,
          note: data.note || data.notes || '',
          email_alert: Boolean(data.email_alert),
          sms_alert: Boolean(data.sms_alert),
          alert_before: alertBefore,
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load appointment', err);
        setLoadError(problemMessage(err, 'Failed to load the appointment'));
        setLoading(false);
      });
  }, [appointmentId]);

  const doctorId = original ? original.doctor_id : null;
  const {date: formDate, timezone: formTz} = appointment;

  useEffect(() => {
    if (!original || !formDate || !formTz) return undefined;
    const day = dayRange(formDate, formTz);
    // A half-typed date is not a day yet; wait for a complete one.
    if (Number.isNaN(day.start)) return undefined;
    let cancelled = false;
    setSlotsLoading(true);
    setSlotsError(null);
    AppointmentApi.getAvailability({
      start: new Date(day.start).toISOString(),
      end: new Date(day.end).toISOString(),
      timezone: formTz,
      doctorId,
    })
      .then((list) => {
        if (!cancelled) setDoctors(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load availability', err);
        setDoctors([]);
        setSlotsError(problemMessage(err, 'Failed to load available times'));
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [original, doctorId, formDate, formTz, availabilityVersion]);

  const durationMinutes = original
    ? Math.max(1, Math.round((Date.parse(original.end_time) - Date.parse(original.start_time)) / 60000) || 30)
    : 30;

  // While availability loads, only the current time is listed (never a stale or guessed slot).
  const options =
    original && !slotsLoading && !slotsError
      ? rescheduleOptions({
          date: appointment.date,
          tz: appointment.timezone,
          doctors,
          doctorId,
          durationMinutes,
          ignore: original,
        })
      : [];

  // The current booking stays selectable (labelled) even though availability lists it as taken.
  const currentValue =
    original && localDate(original.start_time, appointment.timezone) === appointment.date
      ? localTime(original.start_time, appointment.timezone)
      : null;
  const timeOptions = [...options];
  if (currentValue && !timeOptions.some((o) => o.value === currentValue)) {
    timeOptions.push({value: currentValue, start: original.start_time});
    timeOptions.sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  }
  const timeAvailable = timeOptions.some((o) => o.value === appointment.time);

  const handleChangeForm = (e) => {
    const {name, value} = e.target;
    setAppointment((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === 'date' || name === 'time') setConflictError(null);
  };

  const handleTimezoneChange = (tz) => {
    const next = tz.value || tz;
    setAppointment((prev) => {
      if (!prev.date || !prev.time || next === prev.timezone) return {...prev, timezone: next};
      // Keep the same instant; only the displayed wall-clock time changes.
      const instant = toInstant(prev.date, prev.time, prev.timezone);
      return {...prev, timezone: next, date: localDate(instant, next), time: localTime(instant, next)};
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!appointment.date) newErrors.date = 'Choose a date';
    if (!appointment.time) newErrors.time = 'Choose a time';
    else if (!slotsLoading && !timeAvailable) newErrors.time = 'Choose one of the available times';

    if (appointment.alert_before.length > 0 && !appointment.email_alert && !appointment.sms_alert) {
      newErrors.alerts = 'Choose at least one reminder method';
    }

    if (appointment.alert_before.length === 0 && (appointment.email_alert || appointment.sms_alert)) {
      newErrors.alerts = 'Choose at least one reminder timing (e.g. 1 hour before)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveError(null);
    if (validate()) {
      setConfirmModalShow(true);
    }
  };

  const onClickUpdateAppointment = () => {
    if (submitting || !original) return;
    // Interpret date+time in the selected timezone (not the browser's), so reminders computed
    // on the server (start_time - 1h / 1d / 1w) match the patient's intended time.
    const body = AppointmentApi.appointmentPatch(original, {
      start_time: toInstant(appointment.date, appointment.time, appointment.timezone),
      patient_timezone: appointment.timezone,
      notes: appointment.note,
      email_alert: appointment.email_alert,
      sms_alert: appointment.sms_alert,
      alert_before: appointment.alert_before,
    });
    if (Object.keys(body).length === 0) {
      navigate(`/appointments/${appointmentId}`);
      return;
    }
    setSubmitting(true);
    setConflictError(null);
    setSaveError(null);
    AppointmentApi.updateAppointment(appointmentId, body)
      .then(() => {
        navigate(`/appointments/${appointmentId}`);
      })
      .catch((err) => {
        setSubmitting(false);
        const status = err && err.response && err.response.status;
        if (status === 409) {
          setConflictError(CONFLICT_MESSAGE);
          setAppointment((prev) => ({...prev, time: ''}));
          setAvailabilityVersion((v) => v + 1);
          if (timeSelectRef.current) timeSelectRef.current.focus();
          return;
        }
        setSaveError(problemMessage(err, 'Failed to save the appointment'));
      });
  };

  if (loading) {
    return (
      <PageShell title="Edit Appointment Reminder" showBackButton={true}>
        <div style={{textAlign: 'center', marginTop: '2rem'}}>Loading...</div>
      </PageShell>
    );
  }

  if (!original) {
    return (
      <PageShell title="Edit Appointment Reminder" showBackButton={true}>
        <div role="alert" style={{textAlign: 'center', marginTop: '2rem'}}>
          {loadError || 'Appointment not found.'}
        </div>
      </PageShell>
    );
  }

  const timeDescribedBy = ['appt-time-hint', conflictError ? 'appt-conflict' : null]
    .filter(Boolean)
    .join(' ');

  return (
    <PageShell title="Edit Appointment Reminder" showBackButton={true}>
      <form onSubmit={handleSubmit} className="appt-add-container" noValidate>
        <div className="appt-form-card">
          <div className="appt-field">
            <label className="appt-field-label" htmlFor="appt-date">
              Date
            </label>
            <input
              id="appt-date"
              type="date"
              name="date"
              className="appt-input"
              data-testid="appointment-date-input"
              value={appointment.date}
              onChange={handleChangeForm}
              min={dayjs().tz(appointment.timezone).format('YYYY-MM-DD')}
              aria-invalid={errors.date ? true : undefined}
            />
            {errors.date && <div style={errorStyle}>{errors.date}</div>}
          </div>

          <div className="appt-field">
            <label className="appt-field-label" htmlFor="appt-time">
              Time
            </label>
            <select
              id="appt-time"
              name="time"
              ref={timeSelectRef}
              className="appt-select"
              data-testid="appointment-time-select"
              value={timeAvailable ? appointment.time : ''}
              onChange={handleChangeForm}
              aria-describedby={timeDescribedBy}
              aria-invalid={errors.time || conflictError ? true : undefined}
              aria-busy={slotsLoading || undefined}
            >
              <option value="">{slotsLoading ? 'Loading available times…' : 'Choose a time'}</option>
              {timeOptions.map((o) => (
                <option key={o.start} value={o.value}>
                  {o.value === currentValue ? `${o.value} (current time)` : o.value}
                </option>
              ))}
            </select>
            <p className="appt-field-hint" id="appt-time-hint" aria-live="polite">
              {slotsLoading
                ? 'Loading available times…'
                : slotsError
                  ? slotsError
                  : options.length === 0
                    ? 'No other times are available on this date. Try another date.'
                    : 'Only open times are listed. Times are shown in the selected timezone.'}
            </p>
            {conflictError && (
              <div
                id="appt-conflict"
                role="alert"
                data-testid="appointment-conflict-error"
                style={alertBoxStyle}
              >
                {conflictError}
              </div>
            )}
            {errors.time && <div style={errorStyle}>{errors.time}</div>}
          </div>

          <div className="appt-field">
            <label className="appt-field-label" id="appt-timezone-label">
              Timezone
            </label>
            <div style={{borderRadius: '12px'}}>
              <TimezoneSelect
                aria-labelledby="appt-timezone-label"
                value={appointment.timezone}
                onChange={handleTimezoneChange}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '48px',
                    borderRadius: '12px',
                    borderColor: state.isFocused ? '#c43b71' : '#E5E7EB',
                    backgroundColor: '#F9FAFB',
                    fontFamily: 'Inter',
                    fontSize: '15px',
                    boxShadow: state.isFocused ? '0 0 0 3px rgba(196, 59, 113, 0.35)' : 'none',
                    '&:hover': {
                      borderColor: state.isFocused ? '#c43b71' : '#E5E7EB',
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    zIndex: 100,
                  }),
                }}
              />
            </div>
          </div>

          <div className="appt-field">
            <label className="appt-field-label" htmlFor="appt-note">
              Note for yourself
            </label>
            <p className="appt-field-hint">Optional. Private notes only you can see.</p>
            <textarea
              id="appt-note"
              className="appt-textarea"
              placeholder="Add details like insurance info or special instructions…"
              name="note"
              value={appointment.note}
              onChange={handleChangeForm}
            />
          </div>

          <fieldset className="choice-group-fieldset">
            <legend className="choice-group-legend">Alert methods</legend>
            {errors.alerts && <div style={{...errorStyle, marginBottom: '8px'}}>{errors.alerts}</div>}

            <ChoiceRow
              checked={appointment.email_alert}
              onToggle={() => setAppointment((prev) => ({...prev, email_alert: !prev.email_alert}))}
              label="Email"
            />
            <ChoiceRow
              checked={appointment.sms_alert}
              disabled={!hasPhone}
              onToggle={() => setAppointment((prev) => ({...prev, sms_alert: !prev.sms_alert}))}
              label="SMS (US Only)"
              description={hasPhone ? null : 'Add a phone number in your profile to turn on SMS'}
            />
          </fieldset>

          <fieldset className="choice-group-fieldset">
            <legend className="choice-group-legend">Alert before appointment</legend>

            {['1 hour', '1 day', '1 week'].map((time) => {
              const isChecked = appointment.alert_before.includes(time);
              return (
                <ChoiceRow
                  key={time}
                  checked={isChecked}
                  label={time}
                  onToggle={() =>
                    setAppointment((prev) => ({
                      ...prev,
                      alert_before: isChecked
                        ? prev.alert_before.filter((v) => v !== time)
                        : [...prev.alert_before, time],
                    }))
                  }
                />
              );
            })}
          </fieldset>

          {saveError && (
            <div role="alert" data-testid="appointment-save-error" style={alertBoxStyle}>
              {saveError}
            </div>
          )}

          <div style={{display: 'flex', gap: '12px', marginTop: '6px'}}>
            <button
              type="submit"
              className="primary-btn"
              style={{flex: 1}}
              disabled={submitting}
              data-testid="appointment-save"
            >
              {submitting ? 'Saving…' : 'Update reminder'}
            </button>
            <button
              type="button"
              className="primary-btn"
              style={{flex: 1, background: '#6B7280', color: 'white'}}
              onClick={() => navigate(`/appointments/${appointmentId}`)}
            >
              Cancel
            </button>
          </div>
        </div>

        <ConfirmModal
          show={confirmModalShow}
          onHide={() => setConfirmModalShow(false)}
          modalTitle="Confirm Update"
          btnText="Confirm"
          clickBtn={() => {
            setConfirmModalShow(false);
            onClickUpdateAppointment();
          }}
        >
          <div style={{fontSize: '1.2rem', padding: '16px'}}>
            <p>
              Appointment time: {appointment.date} {appointment.time} ({appointment.timezone})
            </p>
            <p>Your appointment reminder notification will be sent to the following:</p>
            {appointment.email_alert && (
              <div style={{display: 'flex', marginBottom: '8px'}}>
                <strong style={{width: '80px'}}>E-mail:</strong>
                <span>{accountData?.email || ''}</span>
              </div>
            )}
            {appointment.sms_alert && accountData?.phone && (
              <div style={{display: 'flex'}}>
                <strong style={{width: '80px'}}>Phone:</strong>
                <span>{accountData.phone}</span>
              </div>
            )}
            {!appointment.email_alert && !appointment.sms_alert && (
              <div style={{color: '#4B5563'}}>No alerts configured.</div>
            )}
          </div>
        </ConfirmModal>
      </form>
    </PageShell>
  );
}
